import { getAppConfigurationFromCache } from "@/api/app-configuration.api";
import type {
  DepartmentSizeQueryFilter,
  EventQueryFilter,
  QueryFilter,
} from "@/types/campaign";
import { FieldName } from "@/types/filters";
import { Entries } from "@/types/utility";
import { tryGet } from "@/utils/common";
import { buildFilterGroupFromLabelInfo } from "@/utils/filter-utils";
import { LucideIcon } from "lucide-react";
import { basicFilterCategory } from "./categories/basic";
import { contactFilterCategory } from "./categories/contact";
import { contactsFilterCategory } from "./categories/contacts";
import { customFilterCategory } from "./categories/custom";
import { economyFilterCategory } from "./categories/economy";
import { geographyFilterCategory } from "./categories/geography";
import { performanceFilterCategory } from "./categories/performance";
import { technologiesFilterCategory } from "./categories/technologies";
import { websiteFilterCategory } from "./categories/website";
import { AnyFilterGroup, TypedFilterGroup } from "./filter-types";

export const filterCategories = {
  basic: basicFilterCategory,
  geography: geographyFilterCategory,
  contact: contactFilterCategory,
  economy: economyFilterCategory,
  performance: performanceFilterCategory,
  website: websiteFilterCategory,
  technologies: technologiesFilterCategory,
  contacts: contactsFilterCategory,
  custom: customFilterCategory,
} as const;

const filterGroupsById = new Map<
  string,
  {
    group: TypedFilterGroup;
    category: {
      id: string;
      key: keyof typeof filterCategories;
    };
  }
>();

const filterGroupsByFilterName = new Map<
  string,
  {
    filterGroup: TypedFilterGroup;
    category: {
      id: string;
      key: keyof typeof filterCategories;
    };
  }
>();

for (const [categoryKey, category] of Object.entries(
  filterCategories,
) as Entries<typeof filterCategories>) {
  for (const [_, filterGroup] of Object.entries(category.filters) as [
    string,
    TypedFilterGroup,
  ][]) {
    filterGroupsById.set(filterGroup.id, {
      group: filterGroup,
      category: {
        id: category.id,
        key: categoryKey,
      },
    });

    function getFilterNames(filterGroup: {
      filterType: { filters: readonly string[] };
      filters: Partial<Record<string, FilterIdentifier | null>>;
    }) {
      const filterNames: string[] = [];
      for (const filterId of filterGroup.filterType.filters) {
        const filter = filterGroup.filters[filterId];
        if (filter) {
          filterNames.push(filter.filterName);
        }
      }
      return filterNames;
    }

    const filterNames = getFilterNames(filterGroup);

    for (const filterName of filterNames) {
      filterGroupsByFilterName.set(filterName, {
        filterGroup,
        category: {
          id: category.id,
          key: categoryKey,
        },
      });
    }
  }
}

export type FilterIdentifier = { fieldName: FieldName; filterName: string };

export { type AnyFilterGroup as FilterGroup };

export type FilterCategory = {
  id: string;
  title: string;
  icon: LucideIcon | null;
  description: string;
  // FYI: Whether the filter is restricted for Essentials solutions.
  isAdvancedFilter?: boolean;
  filters: {
    [key: string]: TypedFilterGroup;
  };
};

export type FilterGroupEntry = [
  keyof FilterCategory["filters"],
  AnyFilterGroup,
];

export function getFilterGroupById(id: string) {
  const filterGroup = filterGroupsById.get(id);
  if (filterGroup) return filterGroup;

  const appConfig = getAppConfigurationFromCache();
  if (appConfig?.labelFilters) {
    for (const labelFilter of appConfig.labelFilters) {
      if (id === labelFilter.labelId) {
        return {
          group: buildFilterGroupFromLabelInfo([labelFilter])[0],
          category: {
            id: "custom",
            key: "custom" as const,
          },
        };
      }
    }
  }
}

export function getFilterGroup(
  filterName: string,
  includeCustomLabelFilters = true,
) {
  const filterGroup = filterGroupsByFilterName.get(filterName);
  if (filterGroup) return filterGroup;

  if (includeCustomLabelFilters) {
    const appConfig = getAppConfigurationFromCache();
    if (appConfig?.labelFilters) {
      for (const labelFilter of appConfig.labelFilters) {
        if (filterName === `hasAiLabel_${labelFilter.labelId}`) {
          return {
            filterGroup: buildFilterGroupFromLabelInfo([labelFilter])[0],
            category: {
              id: "custom",
              key: "custom" as const,
            },
          };
        }
      }
    }
  }

  throw new Error(`No filter group found for filterName ${filterName}`);
}

// ? MWK: I added the union type so it works with my code in the player, change it if necessary
export function groupFiltersByFilterGroup(
  filters: (QueryFilter | EventQueryFilter | DepartmentSizeQueryFilter)[],
) {
  const filterGroups = new Map<
    string,
    {
      filterGroup: TypedFilterGroup;
      filters: (QueryFilter | EventQueryFilter | DepartmentSizeQueryFilter)[];
      categoryId: string;
      categoryKey: keyof typeof filterCategories;
    }
  >();

  for (const filter of filters) {
    const result = tryGet(() => getFilterGroup(filter.filterName));
    if (!result) continue;

    const { filterGroup, category } = result;
    const filterGroupKey = filterGroup.id;
    let filterGroupItem = filterGroups.get(filterGroupKey);
    if (!filterGroupItem) {
      filterGroupItem = {
        filterGroup,
        filters: [],
        categoryId: category.id,
        categoryKey: category.key,
      };
      filterGroups.set(filterGroupKey, filterGroupItem);
    }

    filterGroupItem.filters.push(filter);
  }

  return Array.from(filterGroups.values());
}

export function groupFilterGroupsByCategory(
  filterGroups: ReturnType<typeof groupFiltersByFilterGroup>,
) {
  const categories = new Map<
    string,
    {
      category: FilterCategory;
      categoryKey: keyof typeof filterCategories;
      filterGroups: ReturnType<typeof groupFiltersByFilterGroup>;
    }
  >();
  for (const filterGroup of filterGroups) {
    const category = filterCategories[filterGroup.categoryKey];
    let categoryItem = categories.get(category.id);
    if (categoryItem === undefined) {
      categoryItem = {
        category: category,
        categoryKey: filterGroup.categoryKey,
        filterGroups: [],
      };
      categories.set(category.id, categoryItem);
    }
    categoryItem.filterGroups.push(filterGroup);
  }
  return Array.from(categories.values());
}

// export type FilterComponentProps<Group extends BaseFilterGroup> = {
//   group: Group;
//   backLinkOptions: ToOptions;
//   isNewCampaign: boolean;
//   hideCancelButton: boolean;
//   getFilter: (targetFilter: {
//     filterName: string;
//     fieldName: string;
//   }) => QueryFilter | EventQueryFilter | undefined;
//   setFilter: (
//     targetFilter: { filterName: string; fieldName: string },
//     filter:
//       | Omit<QueryFilter, "fieldName" | "filterName">
//       | Omit<EventQueryFilter, "fieldName" | "filterName">
//       | undefined,
//   ) => void;
// };

// Some filters that share the same fieldname are identified using a
// `filterName` property on the filter (which is also sent to the backend).
export function getFilterByFilterName(
  filterName: string,
  filters: (QueryFilter | EventQueryFilter | DepartmentSizeQueryFilter)[],
) {
  return filters.find((filter) => filter.filterName === filterName);
}

export function getFiltersByFieldName(
  fieldName: FieldName,
  filters: (QueryFilter | EventQueryFilter)[],
) {
  return filters.filter((filter) => filter.fieldName === fieldName);
}
